import React from "react";

import { faNetworkWired, faPencil, faTrashCan } from "@performyard/fontawesome/pro-regular-svg-icons";
import {
	faCircleInfo,
	faFolder,
	faFolderOpen,
	faHardDrive,
	faSparkles,
	faTags,
	faUser,
} from "@performyard/fontawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@performyard/fontawesome/react-fontawesome";
import { BasicDoughnutChart } from "@performyard/py-components";

import IconButton from "../../../../components/Button/IconButton";
import Icon from "../../../../components/Icon/Icon";
import Link from "../../../../components/Link";
// components
import Typography from "../../../../components/Text";
import Tooltip from "../../../../components/Tooltip";

import { useAiSettings, useRbacPrimaryRolePermissions } from "../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

// styles
import css from "../..//Documents.module.scss";
// helpers
import { bytesToKB, extractValidationErrorType, formatCompanyTotalFileSize, getFailureMessage } from "../../helpers";
// schemas
import { DocumentFileRow, DownloadAllMyFilesRow, FolderRow, TotalSizeRow } from "../../schema";

export const Folder = (props: FolderRow) => {
	const { name, id, isOpen, toggleFolderState } = props;

	return (
		<div className={css.row}>
			<div className={css.clickable} onClick={() => toggleFolderState(id)}>
				<Icon icon={isOpen ? faFolderOpen : faFolder} className={css.folderIcon} />
				<Typography size="paragraph-3" weight="light" color="primary-5" className={css.mr8}>
					{name}
				</Typography>
			</div>
		</div>
	);
};

export const TotalSize = ({ totalSize, name, companyMaxSpace }: TotalSizeRow) => {
	return (
		<div className={css.row}>
			<Icon icon={faHardDrive} className={css.totalSizeIcon} />
			<Typography size="paragraph-3" weight="light" color="neutral-7" className={css.mr8}>
				{name} {bytesToKB(totalSize)}{" "}
				{companyMaxSpace !== undefined && `out of ${formatCompanyTotalFileSize(companyMaxSpace)}`}
			</Typography>
		</div>
	);
};

export const DownloadAllMyFiles = (props: DownloadAllMyFilesRow) => {
	const { userId } = props;
	const protocol = window.location.protocol;
	const host = window.location.host;
	const downloadMyDocumentsLink = `${protocol}//${host}/all-user-documents/${userId}/?folder=user-docs`;

	return (
		<div className={css.row}>
			<Link className={css.clickable} href={downloadMyDocumentsLink}>
				<div className={css.blankIcon} />
				<Typography size="paragraph-3" weight="light" color="primary-5" className={css.mr8}>
					Download this folder
				</Typography>
			</Link>
		</div>
	);
};

export const File = (r: DocumentFileRow) => {
	const { userData, isCompanyDocumentPage } = r;
	const { thisUserId } = userData;
	const { data } = useRbacPrimaryRolePermissions("documents");
	const permissionToEditDocument = data?.includes("documents:editDocument");
	const editable = permissionToEditDocument || thisUserId === r.uploader;
	const isDocumentPdf =
		(!r.content_type || r.content_type === "application/pdf") && r.filename.toLowerCase().endsWith(".pdf");

	// Check if AI features should be shown (requires both AI FF and Conversational AI FF)
	const { value: aiFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);
	const { value: conversationalAIFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-conversational-ui-enabled", false);
	const { data: aiSettings } = useAiSettings();
	const isAiEnabled =
		aiFeatureFlagEnabled &&
		conversationalAIFeatureFlagEnabled &&
		aiSettings?.conversationalAiSettings?.askHrEnabled;

	const showAiActions =
		permissionToEditDocument &&
		isCompanyDocumentPage &&
		r.vis_to_everyone !== undefined &&
		isAiEnabled &&
		isDocumentPdf;
	const isProcessing = r.isProcessing ?? false;
	const isEmbeddingLimitReached = r.isEmbeddingLimitReached ?? false;
	// Disable button if limit reached and trying to enable (can still disable existing)
	const isButtonDisabledDueToLimit = isEmbeddingLimitReached && !r.allow_ai_retrieval;

	const handleAiButtonClick = async (newValue: boolean) => {
		if (r.handleToggleAiRetrieval && !isProcessing && !isButtonDisabledDueToLimit) {
			await r.handleToggleAiRetrieval(r, newValue);
		}
	};

	return (
		<div className={css.row}>
			<div className={css.clickable} onClick={() => r.handleViewDocument(r)}>
				<div className={css.blankIcon} />
				<div className={css.filenameContainer}>
					<Typography
						size="paragraph-3"
						weight="light"
						color="primary-5"
						className={`${css.mr8} py-line-clamp-1`}
					>
						{r.filename}
					</Typography>
				</div>
			</div>
			<Typography size="paragraph-3" weight="light" color="neutral-5" className={css.mr8}>
				{bytesToKB(r.length)}
			</Typography>
			{(r.vis_to_emp || r.vis_to_everyone) && (
				<Tooltip message="Visible to employee" placement="top">
					<Icon icon={faUser} size="small" className={`${css.lightIcon} ${css.mr8}`} />
				</Tooltip>
			)}
			{r.vis_to_man && (
				<Tooltip message="Visible to managers" placement="top">
					<Icon icon={faNetworkWired} size="small" className={`${css.lightIcon} ${css.mr8}`} />
				</Tooltip>
			)}
			{r.tags?.length > 0 && (
				<div className={css.row}>
					<Icon icon={faTags} size="small" className={`${css.lightIcon} ${css.mr8}`} />
					<Typography size="paragraph-3" weight="light" color="neutral-5" className={css.mr8}>
						{r.tags.length === 1 ? r.tags : r.tags.join(", ")}
					</Typography>
				</div>
			)}
			<div className={css.spacer} />
			<div className={css.actionsContainer}>
				{showAiActions && (
					<>
						{r.embedding_failed && (
							<Tooltip
								message={getFailureMessage(extractValidationErrorType(r.embedding_error_type))}
								placement="top"
								maxWidth={150}
							>
								<div className={css.aiInfoIcon}>
									<FontAwesomeIcon icon={faCircleInfo} />
								</div>
							</Tooltip>
						)}
						{isProcessing ? (
							<Tooltip
								message="Processing document for use in Ask PerformYard. This process may take a few minutes."
								placement="top"
								maxWidth={150}
							>
								<div className={`${css.aiButton} ${css.aiButtonLoading}`}>
									<BasicDoughnutChart
										percent={r.embeddingProgress ?? 0}
										size={18}
										showLabel={false}
									/>
								</div>
							</Tooltip>
						) : (
							<Tooltip
								message={
									isButtonDisabledDueToLimit
										? "Maximum Number of Documents Processing. Please wait until they have finished processing to enable Ask PerformYard for another document."
										: r.allow_ai_retrieval
											? "This document is included in Ask PerformYard's knowledge base. Click to exclude it."
											: "This document is excluded from Ask PerformYard's knowledge base. Click to include it."
								}
								placement="top"
								maxWidth={200}
							>
								<button
									type="button"
									className={`${css.aiButton} ${r.allow_ai_retrieval ? css.aiButtonOn : css.aiButtonOff}`}
									onClick={() => handleAiButtonClick(!r.allow_ai_retrieval)}
									disabled={isProcessing || isButtonDisabledDueToLimit}
									aria-label={r.allow_ai_retrieval ? "Disable AI retrieval" : "Enable AI retrieval"}
									aria-pressed={r.allow_ai_retrieval}
									data-testid="ai-document-accessibility"
								>
									<span className={css.aiButtonIconWrapper}>
										<FontAwesomeIcon icon={faSparkles} />
									</span>
								</button>
							</Tooltip>
						)}
					</>
				)}
				{editable && (
					<>
						<IconButton
							icon={faPencil}
							onClick={() => r.handleSelectEdit(r)}
							className={css.editIcon}
							aria-label="Edit document"
						/>
						<IconButton
							icon={faTrashCan}
							onClick={() => r.handleSelectDelete(r)}
							className={css.deleteIcon}
							disabled={isProcessing}
							aria-label="Delete document"
						/>
					</>
				)}
			</div>
		</div>
	);
};
